import { useMemo } from 'react';
import { API } from '@shared/types/api';
import { formatChannelNameForDisplay } from '@shared/util/emoji';
import { i18n } from '@shared/util/i18n';
import { PARTICIPANT_NAME_LIST_MAX, formatNameList, getFirstName } from '@shared/util/name';
import { useDmChannelUsers } from './useDmChannelUsers';

/**
 * useChannelDisplayName
 *
 * Human-readable workspace title: formatted name for non-DM channels; for DMs,
 * the other participant's name, or the "Personal Workspace" string when there
 * is no other participant.
 *
 * For legacy CHANNEL-type channels with an empty name (a deprecated channel
 * type that can no longer be created via the UI), falls back to a
 * comma-separated list of other members' first names, mirroring DM behaviour.
 */

type ChannelInput = Pick<API.ChannelFragment, 'id' | 'type' | 'name' | 'imageUrl'>;

export function useChannelDisplayName(channel: ChannelInput | undefined): string {
  const isDm = channel?.type === 'DM';
  // Legacy CHANNEL type channels may have no name — look up members so we can
  // build a human-readable fallback exactly as we do for DM channels.
  const isLegacyChannelWithNoName = channel?.type === 'CHANNEL' && !channel?.name;
  const needsMemberLookup = isDm || isLegacyChannelWithNoName;
  const { users, loading } = useDmChannelUsers(needsMemberLookup ? channel?.id : undefined);

  return useMemo(() => {
    if (!channel) {
      return '';
    }

    if (!needsMemberLookup) {
      return formatChannelNameForDisplay(channel);
    }

    const otherUsers = users.filter(u => !u.isMe);

    if (isDm) {
      if (otherUsers.length > 0) {
        return formatNameList(otherUsers.slice(0, PARTICIPANT_NAME_LIST_MAX).map(u => u.fullName));
      }

      if (loading) {
        return formatChannelNameForDisplay(channel);
      }

      return i18n('channel.list.personalWorkspace');
    }

    // isLegacyChannelWithNoName: show comma-separated first names of other members.
    if (otherUsers.length > 0) {
      return formatNameList(
        otherUsers.slice(0, PARTICIPANT_NAME_LIST_MAX).map(u => getFirstName(u.fullName)),
      );
    }

    if (loading) {
      return formatChannelNameForDisplay(channel);
    }

    return i18n('channel.list.personalWorkspace');
  }, [channel, isDm, needsMemberLookup, loading, users]);
}
